import React, { useState } from "react";
import { useTranslation } from "@helpers/translate";
import "./index.sass";

const NETWORK_NAMES = {
  TRC20: {
    shortName: "TRC-20",
    displayName: "Tron (TRC-20)",
  },
  "TRC-20": {
    shortName: "TRC-20",
    displayName: "Tron (TRC-20)",
  },
  BEP20: {
    shortName: "BEP-20",
    displayName: "BNB Smart Chain (BEP-20)",
  },
  "BEP-20": {
    shortName: "BEP-20",
    displayName: "BNB Smart Chain (BEP-20)",
  },
  ERC20: {
    shortName: "ERC-20",
    displayName: "Ethereum (ERC-20)",
  },
  "ERC-20": {
    shortName: "ERC-20",
    displayName: "Ethereum (ERC-20)",
  },
  BASE: {
    shortName: "Base",
    displayName: "Base",
  },
  TON: {
    shortName: "TON",
    displayName: "TON",
  },
  BTC: {
    shortName: "BTC",
    displayName: "Bitcoin",
  },
  LTC: {
    shortName: "LTC",
    displayName: "Litecoin",
  },
};

const getNetworkInfo = (network) => {
  const normalizedNetwork = String(network || "").toUpperCase();

  return (
    NETWORK_NAMES[normalizedNetwork] || {
      shortName: network || "",
      displayName: network || "selected",
    }
  );
};

const formatAmount = (value) => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return value || "0";
  }

  return numberValue.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8,
  });
};

const PaymentMethodIcon = ({ icon, title }) => {
  if (!icon) return null;

  if (typeof icon === "string") {
    return (
      <span className="payment-process-step__method-icon-wrap">
        <img
          className="payment-process-step__method-icon"
          src={icon}
          alt={title || ""}
        />
      </span>
    );
  }

  const Icon = icon;

  return (
    <span className="payment-process-step__method-svg-icon-wrap">
      <Icon className="payment-process-step__method-svg-icon" />
    </span>
  );
};

const PaymentProcessStep = ({
  selectedMethod,
  amount,
  depositQuote,
  depositAddress,
  onBack,
  loading = false,
}) => {
  const { __ } = useTranslation();

  const [copiedField, setCopiedField] = useState(null);
  const methodName = selectedMethod?.title || "Payment method";
  const methodIcon =
    selectedMethod?.icon ||
    selectedMethod?.logo ||
    selectedMethod?.image ||
    null;

  const asset =
    depositQuote?.crypto_currency ||
    depositQuote?.pay_crypto_currency ||
    depositQuote?.asset ||
    depositAddress?.asset ||
    selectedMethod?.asset ||
    "USDT";

  const network = depositAddress?.network || selectedMethod?.network || "";
  const { shortName: networkShortName, displayName: networkDisplayName } =
    getNetworkInfo(network);

  const address =
    depositAddress?.deposit_address || depositAddress?.address || "";

  const processingTime = selectedMethod?.processingTime || "Instantly";

  const usdAmount =
    depositQuote?.usd_amount || depositQuote?.amount || amount || 0;

  const cryptoAmount =
    depositQuote?.crypto_amount || depositQuote?.pay_crypto_amount || 0;

  const amountToTransfer = `${formatAmount(cryptoAmount)} ${asset}`;
  const amountToReceive = `${formatAmount(usdAmount)} USD`;

  const qrValue = address;
  const qrUrl = qrValue
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
        qrValue,
      )}`
    : "";

  const copyToClipboard = async (value, field) => {
    if (!value) return;

    setCopiedField(field);

    setTimeout(() => {
      setCopiedField((current) => (current === field ? null : current));
    }, 2000);

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return;
      }

      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";
      textarea.style.left = "-9999px";

      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  return (
    <div className="payment-process-step">
      <div className="payment-process-step__main">
        <div className="payment-process-step__card payment-process-step__details">
          <button
            type="button"
            className="payment-details-step__back"
            onClick={onBack}
            disabled={loading}
          >
            <svg
              width="16"
              height="15"
              viewBox="0 0 16 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 12.6668C9.87202 12.6668 9.744 12.6182 9.64666 12.5202L4.98 7.85353C4.78466 7.6582 4.78466 7.34151 4.98 7.14617L9.64666 2.47951C9.842 2.28417 10.1587 2.28417 10.354 2.47951C10.5494 2.67484 10.5494 2.99153 10.354 3.18686L6.04071 7.50018L10.354 11.8135C10.5494 12.0088 10.5494 12.3255 10.354 12.5208C10.256 12.6182 10.128 12.6668 10 12.6668Z"
                fill="#FCFCFD"
              />
            </svg>
            {__("wallet.deposit.back")}
          </button>

          <div className="payment-process-step__method">
            <PaymentMethodIcon icon={methodIcon} title={methodName} />

            <div>
              <h3>{methodName}</h3>

              <p>
                {__("wallet.deposit.send")} {asset}
                {networkDisplayName
                  ? ` ${__("wallet.deposit.using")} ${networkDisplayName} ${__(
                      "wallet.deposit.network_word",
                    )}`
                  : ""}
              </p>
            </div>
          </div>

          <div className="payment-process-step__important">
            <h4>{__("wallet.deposit.important")}</h4>

            <p>
              {__("wallet.deposit.use_only_the")}
              <span> {networkDisplayName || "selected"}</span>{" "}
              {__("wallet.deposit.network_for_transfer")}
            </p>

            <strong>{__("wallet.deposit.failure_to_meet_requirement")}</strong>
          </div>

          <div className="payment-process-step__field">
            <p>{__("wallet.deposit.amount_to_transfer")}</p>

            <div className="payment-process-step__copy-field">
              <span>{amountToTransfer}</span>

              <button
                type="button"
                className="payment-process-step__copy-button"
                onClick={() => copyToClipboard(amountToTransfer, "amount")}
              >
                {copiedField === "amount" ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M13.3334 4L6.00008 11.3333L2.66675 8"
                      stroke="#A6A6A6"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.25 4.16667H6.41667C4.96533 4.16667 4.16667 4.96533 4.16667 6.41667V12.25C4.16667 13.7013 4.96533 14.5 6.41667 14.5H12.25C13.7013 14.5 14.5 13.7013 14.5 12.25V6.41667C14.5 4.96533 13.7013 4.16667 12.25 4.16667ZM13.5 12.25C13.5 13.138 13.138 13.5 12.25 13.5H6.41667C5.52867 13.5 5.16667 13.138 5.16667 12.25V6.41667C5.16667 5.52867 5.52867 5.16667 6.41667 5.16667H12.25C13.138 5.16667 13.5 5.52867 13.5 6.41667V12.25ZM2.5 3.74666V9.58667C2.5 10.3853 2.82206 10.582 2.92806 10.6473C3.16406 10.7913 3.23726 11.0993 3.09326 11.3347C2.9986 11.4887 2.83468 11.5733 2.66602 11.5733C2.57735 11.5733 2.48661 11.5493 2.40527 11.5C1.80461 11.132 1.5 10.4887 1.5 9.58667V3.74666C1.5 2.31866 2.31941 1.5 3.74674 1.5H9.58659C10.7099 1.5 11.2467 1.99265 11.5 2.40531C11.644 2.64065 11.57 2.94865 11.3346 3.09265C11.0986 3.23732 10.792 3.16266 10.6473 2.92733C10.5826 2.82133 10.3853 2.49935 9.58659 2.49935H3.74674C2.86141 2.50002 2.5 2.86133 2.5 3.74666Z"
                      fill="white"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="payment-process-step__field">
            <p>
              {__("wallet.deposit.send_to_this")}{" "}
              {networkShortName || "selected"} {__("wallet.deposit.address")}
            </p>

            <div className="payment-process-step__copy-field payment-process-step__copy-field--address">
              <span>{address || "Address is not available"}</span>

              <button
                type="button"
                className="payment-process-step__copy-button"
                onClick={() => copyToClipboard(address, "address")}
              >
                {copiedField === "address" ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M13.3334 4L6.00008 11.3333L2.66675 8"
                      stroke="#A6A6A6"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.25 4.16667H6.41667C4.96533 4.16667 4.16667 4.96533 4.16667 6.41667V12.25C4.16667 13.7013 4.96533 14.5 6.41667 14.5H12.25C13.7013 14.5 14.5 13.7013 14.5 12.25V6.41667C14.5 4.96533 13.7013 4.16667 12.25 4.16667ZM13.5 12.25C13.5 13.138 13.138 13.5 12.25 13.5H6.41667C5.52867 13.5 5.16667 13.138 5.16667 12.25V6.41667C5.16667 5.52867 5.52867 5.16667 6.41667 5.16667H12.25C13.138 5.16667 13.5 5.52867 13.5 6.41667V12.25ZM2.5 3.74666V9.58667C2.5 10.3853 2.82206 10.582 2.92806 10.6473C3.16406 10.7913 3.23726 11.0993 3.09326 11.3347C2.9986 11.4887 2.83468 11.5733 2.66602 11.5733C2.57735 11.5733 2.48661 11.5493 2.40527 11.5C1.80461 11.132 1.5 10.4887 1.5 9.58667V3.74666C1.5 2.31866 2.31941 1.5 3.74674 1.5H9.58659C10.7099 1.5 11.2467 1.99265 11.5 2.40531C11.644 2.64065 11.57 2.94865 11.3346 3.09265C11.0986 3.23732 10.792 3.16266 10.6473 2.92733C10.5826 2.82133 10.3853 2.49935 9.58659 2.49935H3.74674C2.86141 2.50002 2.5 2.86133 2.5 3.74666Z"
                      fill="white"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <p className="payment-process-step__notice">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 15.1669C7.94667 15.1669 7.89329 15.1582 7.84196 15.1409C6.78263 14.7875 1.5 12.7482 1.5 7.25887V3.33357C1.5 3.09557 1.66802 2.8895 1.90202 2.8435C5.15535 2.19283 6.3087 1.6209 7.76937 0.896236C7.91004 0.826236 8.09664 0.816958 8.2373 0.886958C9.67797 1.61296 10.816 2.18683 14.0986 2.8435C14.3326 2.89017 14.5007 3.09557 14.5007 3.33357V7.25952C14.5007 12.7488 9.21802 14.7882 8.15869 15.1415C8.10669 15.1582 8.05333 15.1669 8 15.1669ZM2.5 3.74161V7.25887C2.5 11.8355 6.77867 13.699 8 14.137C9.22133 13.699 13.5 11.8349 13.5 7.25887V3.74161C10.5527 3.12495 9.31326 2.54617 8.00993 1.89217C6.62526 2.57683 5.42333 3.13095 2.5 3.74161ZM7.69466 9.68692L10.3613 7.02026C10.5567 6.82493 10.5567 6.50824 10.3613 6.3129C10.166 6.11757 9.84931 6.11757 9.65397 6.3129L7.34066 8.62622L6.36068 7.64624C6.16534 7.4509 5.84865 7.4509 5.65332 7.64624C5.45799 7.84157 5.45799 8.15826 5.65332 8.35359L6.98665 9.68692C7.08399 9.78426 7.21201 9.83357 7.34001 9.83357C7.46801 9.83357 7.59733 9.78493 7.69466 9.68692Z"
                fill="#FF6C22"
              />
            </svg>
            {__("wallet.deposit.funds_credited_after_confirmations")}
          </p>
        </div>

        <div className="payment-process-step__card payment-process-step__summary">
          <p className="payment-process-step__summary-title">
            {__("wallet.deposit.payment_summary")}
          </p>

          <div className="payment-process-step__receive">
            <p>{__("wallet.deposit.you_will_receive")}</p>

            <strong>{amountToReceive}</strong>
          </div>

          <div className="payment-process-step__summary-list">
            <div>
              <span>{__("wallet.deposit.network_label")}</span>
              <strong>{networkShortName || "-"}</strong>
            </div>

            <div>
              <span>{__("wallet.deposit.processing_time_label")}</span>
              <strong>{processingTime}</strong>
            </div>
          </div>

          <div className="payment-process-step__qr">
            <p>{__("wallet.deposit.scan_to_pay")}</p>

            {qrUrl ? (
              <img src={qrUrl} alt="Payment QR code" />
            ) : (
              <div className="payment-process-step__qr-empty">
                {__("wallet.deposit.qr_unavailable")}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentProcessStep;
